import { z } from 'zod';

import { UuidSchema } from '../../common/id/id';
import { VisibleAnswerSchema } from '../../profiles/visible-answers';
import { ViewerContextSchema } from '../../visibility/viewer-context/viewer-context';

/**
 * The man a ward is talking to, as her **guardian** is allowed to read him.
 *
 * ## It is `MemberProfileView` with the photographs removed, and that is the decision
 *
 * A guardian is not a member of this pair and never will be, so the composition
 * still happens exactly once — in `MemberViewService`, the class N18 keeps
 * singular — and this schema is a **narrowing** of what came back, not a second
 * answer to "what may this viewer see". `about`, `answers` and the echoed
 * `viewerContext` arrive already resolved; nothing here decides anything.
 *
 * What it drops is `photos` and `photosBlurred`, because the media mint (N12)
 * would refuse a guardian: he is neither the owner nor a matched party, and the
 * ladder that decides that has no guardian rung. Shipping descriptors he cannot
 * exchange for bytes is the permanent broken image `MemberViewService`'s
 * `WHEN_UNSET` docblock warns about — a payload that says "there is a
 * photograph" while every request for it is refused. Identity by words, not by
 * face. Giving N12's ladder a two-hop guardian rung is a change to the highest-
 * risk authorization file in the api and is a phase of its own, not a corner of
 * this one.
 *
 * ## How much of him she is allowed to show her wali is an operator's decision
 *
 * `viewerContext` is not inferred here and is not the guardian's relationship to
 * this man — he has none. It is the **cap** the deployment places on the
 * disclosure, `guardian.chatCounterpartyContext`, resolved against the **ward's**
 * gender (U9, U15): `public` hands the wali what this man already publishes to
 * every member on the platform, `matched` hands him exactly what his ward sees.
 * It is echoed for `VisibleAnswersSchema`'s reason — the two are the same shape,
 * and rendering one as the other would misrepresent how much a wali was told.
 */
export const GuardianCounterpartySchema = z
  .object({
    userId: UuidSchema,
    viewerContext: ViewerContextSchema,
    /** His own paragraph — `null` in the `public` context, always. */
    about: z.string().nullable(),
    answers: z.array(VisibleAnswerSchema),
  })
  .strict()
  .superRefine((counterparty, ctx) => {
    // The same lock `MemberProfileViewSchema` carries, restated rather than
    // imported: this payload has a second producer, and a rule enforced in only
    // one of two composers is a rule that drifts.
    if (counterparty.viewerContext === 'public' && counterparty.about !== null) {
      ctx.addIssue({
        code: 'custom',
        path: ['about'],
        message: 'A member’s own paragraph is shown only to a matched viewer.',
      });
    }
  });

export type GuardianCounterparty = z.infer<typeof GuardianCounterpartySchema>;
